import React, { useState } from 'react';
import HoloCard from '../components/HoloCard';
import SafeIcon from '../common/SafeIcon';
import { motion, AnimatePresence } from 'framer-motion';
import { workflowSteps, sampleRecords, faqData, sampleModuleData } from '../utils/demoMockData';

const DemoPage = () => {
  const [activeStep, setActiveStep] = useState(0);
  const [filterPriority, setFilterPriority] = useState('All');
  const [simState, setSimState] = useState('success');
  const [activeFaq, setActiveFaq] = useState(null);

  const filteredRecords = sampleRecords.filter(r => filterPriority === 'All' || r.priority === filterPriority);

  return (
    <div className="space-y-12 pb-24 max-w-7xl mx-auto px-4 md:px-0">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-end justify-between gap-4 border-b border-cyan-900/30 pb-6">
        <div>
          <h1 className="text-3xl font-light text-white tracking-tight">Demo / <span className="font-bold text-cyan-400 uppercase">How It Works</span></h1>
          <p className="text-slate-400 text-sm mt-2 max-w-2xl">OpsPrism is a mission-control center for modern operations teams. We solve the chaos of repeatable workflows by providing a unified holographic interface for monitoring, detection, and remediation.</p>
        </div>
        <div className="flex items-center space-x-3 bg-cyan-950/20 px-4 py-2 rounded border border-cyan-900/40">
          <div className="w-2 h-2 rounded-full bg-cyan-400 animate-pulse" />
          <span className="text-[10px] font-mono text-cyan-500 uppercase tracking-widest">Environment: Ops-Prism-Sim-Alpha</span>
        </div>
      </div>

      {/* Section 1: Overview */}
      <section className="grid grid-cols-1 md:grid-cols-3 gap-6">
        <div className="md:col-span-2 space-y-4">
          <h2 className="text-xl font-medium text-white flex items-center gap-2">
            <SafeIcon name="Info" className="text-cyan-500" />
            Overview / What it does
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <HoloCard title="The Problem">
              <p className="text-sm text-slate-400 leading-relaxed">Ops teams often fly blind, managing critical workflows across fragmented tools. This leads to missed SLAs, hidden bottlenecks, and reactive firefighting.</p>
            </HoloCard>
            <HoloCard title="The Outcome">
              <p className="text-sm text-slate-400 leading-relaxed">OpsPrism centralizes every task queue into a high-fidelity dashboard, enabling proactive response and 40% faster incident resolution.</p>
            </HoloCard>
          </div>
        </div>
        <HoloCard title="Target User" className="bg-cyan-900/10">
          <ul className="space-y-3 text-sm text-slate-300">
            <li className="flex items-center gap-2"><SafeIcon name="Check" className="text-cyan-500 text-xs" /> Site Reliability Engineers</li>
            <li className="flex items-center gap-2"><SafeIcon name="Check" className="text-cyan-500 text-xs" /> Business Ops Managers</li>
            <li className="flex items-center gap-2"><SafeIcon name="Check" className="text-cyan-500 text-xs" /> Logistics Coordinators</li>
          </ul>
        </HoloCard>
      </section>

      {/* Section 2: Try this flow */}
      <section className="space-y-6">
        <h2 className="text-xl font-medium text-white flex items-center gap-2">
          <SafeIcon name="Zap" className="text-cyan-500" />
          Try this flow
        </h2>
        <HoloCard className="!p-0">
          <div className="flex flex-col lg:flex-row">
            <div className="lg:w-1/3 border-r border-cyan-900/30 p-4 space-y-2 bg-slate-900/20">
              {workflowSteps.map((step, idx) => (
                <button
                  key={step.id}
                  onClick={() => setActiveStep(idx)}
                  className={`w-full text-left p-3 rounded transition-all flex items-center justify-between group ${
                    activeStep === idx ? 'bg-cyan-950 border border-cyan-500/50 text-cyan-100' : 'text-slate-500 hover:bg-slate-900'
                  }`}
                >
                  <div className="flex items-center gap-3">
                    <span className="font-mono text-xs opacity-50">{idx + 1}</span>
                    <span className="text-sm font-medium">{step.label}</span>
                  </div>
                  <SafeIcon name="ChevronRight" className={`transition-transform ${activeStep === idx ? 'translate-x-1' : 'opacity-0'}`} />
                </button>
              ))}
            </div>
            <div className="lg:w-2/3 p-8 relative overflow-hidden">
              <div className="absolute top-0 right-0 p-4">
                <SafeIcon name="Cpu" className="text-6xl text-cyan-500/5" />
              </div>
              <AnimatePresence mode="wait">
                <motion.div
                  key={activeStep}
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, y: -10 }}
                  className="space-y-6"
                >
                  <div className="inline-block px-2 py-1 bg-cyan-500/10 border border-cyan-500/30 rounded text-[10px] font-bold text-cyan-400 uppercase tracking-widest">
                    Active Protocol Stage
                  </div>
                  <h3 className="text-2xl font-bold text-white">{workflowSteps[activeStep].label}</h3>
                  <p className="text-slate-400 text-lg leading-relaxed max-w-xl">
                    {workflowSteps[activeStep].description}
                  </p>
                  <div className="flex gap-4 pt-4">
                    <button className="bg-cyan-600 hover:bg-cyan-500 text-slate-950 px-6 py-2 rounded font-bold text-sm transition-all shadow-[0_0_15px_rgba(6,182,212,0.4)]">
                      Execute Protocol
                    </button>
                    <button className="border border-slate-700 hover:border-slate-500 text-slate-400 px-6 py-2 rounded font-bold text-sm transition-all">
                      View Documentation
                    </button>
                  </div>
                </motion.div>
              </AnimatePresence>
            </div>
          </div>
        </HoloCard>
      </section>

      {/* Section 3: Sample Data */}
      <section className="grid grid-cols-1 lg:grid-cols-3 gap-8">
        <div className="lg:col-span-2 space-y-6">
          <div className="flex justify-between items-center">
            <h2 className="text-xl font-medium text-white flex items-center gap-2">
              <SafeIcon name="Database" className="text-cyan-500" />
              Sample Data
            </h2>
            <div className="flex bg-slate-900 rounded p-1 border border-slate-800">
              {['All', 'High', 'Critical'].map(p => (
                <button
                  key={p}
                  onClick={() => setFilterPriority(p)}
                  className={`px-3 py-1 text-[10px] uppercase font-bold rounded transition-all ${
                    filterPriority === p ? 'bg-cyan-500 text-slate-950' : 'text-slate-500 hover:text-slate-300'
                  }`}
                >
                  {p}
                </button>
              ))}
            </div>
          </div>
          <HoloCard className="!p-0 overflow-hidden">
            <div className="overflow-x-auto">
              <table className="w-full text-left">
                <thead className="bg-slate-900/50 border-b border-cyan-900/20">
                  <tr>
                    <th className="p-4 text-[10px] uppercase text-cyan-500 font-bold">ID</th>
                    <th className="p-4 text-[10px] uppercase text-cyan-500 font-bold">Task</th>
                    <th className="p-4 text-[10px] uppercase text-cyan-500 font-bold">Priority</th>
                    <th className="p-4 text-[10px] uppercase text-cyan-500 font-bold">Status</th>
                  </tr>
                </thead>
                <tbody className="text-sm">
                  {filteredRecords.map((record) => (
                    <tr key={record.id} className="border-b border-slate-800/30 hover:bg-white/5 transition-colors">
                      <td className="p-4 font-mono text-cyan-400/60">{record.id}</td>
                      <td className="p-4 text-slate-200">{record.task}</td>
                      <td className="p-4">
                        <span className={`px-2 py-0.5 rounded text-[10px] font-bold ${
                          record.priority === 'Critical' ? 'bg-rose-900/30 text-rose-400' : 
                          record.priority === 'High' ? 'bg-amber-900/30 text-amber-400' : 'bg-slate-800 text-slate-400'
                        }`}>
                          {record.priority}
                        </span>
                      </td>
                      <td className="p-4">
                        <div className="flex items-center gap-2">
                          <div className={`w-1.5 h-1.5 rounded-full ${
                            record.status === 'Completed' ? 'bg-emerald-500' : 'bg-cyan-400 animate-pulse'
                          }`} />
                          <span className="text-xs text-slate-400">{record.status}</span>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </HoloCard>
        </div>

        {/* Section 4: Simulated States */}
        <div className="space-y-6">
          <h2 className="text-xl font-medium text-white flex items-center gap-2">
            <SafeIcon name="Layers" className="text-cyan-500" />
            Simulated States
          </h2>
          <HoloCard className="flex flex-col gap-4">
            <div className="grid grid-cols-2 gap-2">
              {['success', 'warning', 'loading', 'empty'].map(state => (
                <button
                  key={state}
                  onClick={() => setSimState(state)}
                  className={`py-2 rounded text-[10px] font-bold uppercase transition-all border ${
                    simState === state ? 'bg-cyan-500 text-slate-950 border-cyan-400' : 'bg-slate-900 text-slate-500 border-slate-800'
                  }`}
                >
                  {state}
                </button>
              ))}
            </div>
            
            <div className="flex-1 min-h-[160px] flex flex-col items-center justify-center p-6 bg-slate-950/40 rounded-lg border border-cyan-900/20 text-center">
              <AnimatePresence mode="wait">
                {simState === 'success' && (
                  <motion.div key="s" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-2">
                    <SafeIcon name="CheckCircle" className="text-emerald-500 text-4xl mx-auto" />
                    <p className="text-sm text-slate-200">All Systems Nominal</p>
                    <p className="text-[10px] text-slate-500">Latency: 42ms | Load: 12%</p>
                  </motion.div>
                )}
                {simState === 'warning' && (
                  <motion.div key="w" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-2">
                    <SafeIcon name="AlertTriangle" className="text-amber-500 text-4xl mx-auto animate-pulse" />
                    <p className="text-sm text-amber-400">Bottleneck Detected</p>
                    <p className="text-[10px] text-slate-500">Node Cluster: EU-WEST-1</p>
                  </motion.div>
                )}
                {simState === 'loading' && (
                  <motion.div key="l" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-3">
                    <div className="w-10 h-10 border-2 border-cyan-500/20 border-t-cyan-500 rounded-full animate-spin mx-auto" />
                    <p className="text-[10px] font-mono text-cyan-500">REFRESHING UPLINK...</p>
                  </motion.div>
                )}
                {simState === 'empty' && (
                  <motion.div key="e" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }} className="space-y-2">
                    <SafeIcon name="MinusSquare" className="text-slate-700 text-4xl mx-auto" />
                    <p className="text-sm text-slate-500">No telemetry data</p>
                  </motion.div>
                )}
              </AnimatePresence>
            </div>
          </HoloCard>
        </div>
      </section>

      {/* Section 5: FAQ */}
      <section className="space-y-6">
        <h2 className="text-xl font-medium text-white flex items-center gap-2">
          <SafeIcon name="HelpCircle" className="text-cyan-500" />
          FAQ
        </h2>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          {faqData.map((item, i) => (
            <HoloCard key={i} className="cursor-pointer" onClick={() => setActiveFaq(activeFaq === i ? null : i)}>
              <div className="flex justify-between items-center">
                <h4 className="text-sm font-medium text-slate-200">{item.q}</h4>
                <SafeIcon name={activeFaq === i ? "Minus" : "Plus"} className="text-cyan-500" />
              </div>
              <AnimatePresence>
                {activeFaq === i && (
                  <motion.div
                    initial={{ height: 0, opacity: 0 }}
                    animate={{ height: 'auto', opacity: 1 }}
                    exit={{ height: 0, opacity: 0 }}
                    className="overflow-hidden"
                  >
                    <p className="text-xs text-slate-400 mt-4 leading-relaxed border-t border-slate-800 pt-4">
                      {item.a}
                    </p>
                  </motion.div>
                )}
              </AnimatePresence>
            </HoloCard>
          ))}
        </div>
      </section>

      {/* Final Credit Footer */}
      <footer className="pt-12 border-t border-slate-900 flex flex-col md:flex-row justify-between items-center gap-4 opacity-50 group hover:opacity-100 transition-opacity">
        <div className="flex items-center gap-4 text-[10px] font-mono text-slate-500">
          <span>OPSPRISM v4.0.2</span>
          <span className="hidden md:inline">|</span>
          <span>CORE_DEMO_BUILD: 0x5521</span>
        </div>
        <div className="text-[10px] font-mono text-cyan-600/80 tracking-widest uppercase">
          Made by LingLink AI and DigLab AI
        </div>
      </footer>
    </div>
  );
};

export default DemoPage;